import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Package, User, ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';

interface MenuItem {
  label: string;
  path?: string;
  icon: LucideIcon;
  children?: { label: string; path: string }[];
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  {
    label: 'Aplikasi',
    icon: Package,
    children: [{ label: 'Booku', path: '/aplikasi/booku' }],
  },
  { label: 'Profil', path: '/profil', icon: User },
];

export function Sidebar() {
  const location = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    menuItems.forEach((m) => {
      if (m.children?.some((c) => location.pathname.startsWith(c.path))) {
        init[m.label] = true;
      }
    });
    return init;
  });

  const toggleExpand = (label: string) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col">
      <div className="h-14 px-4 flex items-center gap-2 border-b border-gray-200">
        <div className="w-8 h-8 rounded bg-booku-primary flex items-center justify-center text-white font-bold">
          B
        </div>
        <div className="flex flex-col">
          <span className="font-semibold text-gray-800 leading-tight">Booku Client</span>
          <span className="text-xs text-gray-500 leading-tight">Portal Keanggotaan</span>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {menuItems.map((item) => {
          const Icon = item.icon;

          if (item.children) {
            const isExpanded = expanded[item.label];
            const hasActiveChild = item.children.some((c) =>
              location.pathname.startsWith(c.path)
            );

            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleExpand(item.label)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    hasActiveChild
                      ? 'text-booku-primary font-medium bg-booku-primary-surface'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {isExpanded && (
                  <div className="bg-gray-50">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `flex items-center pl-12 pr-4 py-2 text-sm transition-colors ${
                            isActive
                              ? 'text-booku-primary font-medium bg-booku-primary-surface'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.label}
              to={item.path!}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'text-booku-primary font-medium bg-booku-primary-surface'
                    : 'text-gray-700 hover:bg-gray-50'
                }`
              }
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
